/**
 * <ugc-feed> — renders UGC pulled from Archive via the app proxy.
 *
 * The feed is fetched client-side from /apps/ugc, which is served by the
 * hydrogen-api-mph app on Oxygen. The Archive API key never reaches the
 * browser; the proxy holds it.
 *
 * Fetching is deferred until the section is near the viewport, so a UGC block
 * below the fold costs nothing on initial load.
 */
(function () {
  'use strict';

  const PROXY_PATH = '/apps/ugc';
  const ROOT_MARGIN = '400px';
  const LOAD_MARGIN = 400;

  if (customElements.get('ugc-feed')) return;

  customElements.define(
    'ugc-feed',
    class UgcFeed extends HTMLElement {
      constructor() {
        super();
        this.loaded = false;
      }

      connectedCallback() {
        // Children are queried here, not in the constructor: the parser can
        // upgrade the element before its children exist, which left list and
        // template null and silently skipped loading.
        this.list = this.querySelector('[data-ugc-list]');
        this.template = this.querySelector('[data-ugc-item-template]');
        if (!this.list || !this.template) return;

        // The theme editor re-renders sections without a scroll, so don't wait.
        if (window.Shopify && window.Shopify.designMode) {
          this.load();
          return;
        }

        // Load immediately if we already start within range. Some contexts
        // (backgrounded or throttled tabs) never deliver an
        // IntersectionObserver callback, and this must not depend on it.
        if (this.isWithinRange()) {
          this.load();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.teardown();
              this.load();
            }
          },
          {rootMargin: ROOT_MARGIN},
        );
        this.observer.observe(this);

        // Belt and braces: a passive scroll listener covers the case where the
        // observer is throttled and never fires.
        this.onScroll = () => {
          if (this.isWithinRange()) {
            this.teardown();
            this.load();
          }
        };
        window.addEventListener('scroll', this.onScroll, {passive: true});
        window.addEventListener('resize', this.onScroll, {passive: true});
      }

      isWithinRange() {
        const rect = this.getBoundingClientRect();
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight;
        return (
          rect.top < viewportHeight + LOAD_MARGIN && rect.bottom > -LOAD_MARGIN
        );
      }

      teardown() {
        if (this.observer) this.observer.disconnect();
        if (this.onScroll) {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
          this.onScroll = null;
        }
      }

      disconnectedCallback() {
        this.teardown();
        if (this.updateArrows) {
          window.removeEventListener('resize', this.updateArrows);
        }
      }

      get endpoint() {
        const params = new URLSearchParams();
        const presetId = this.dataset.presetId;
        const limit = this.dataset.limit;
        if (presetId) params.set('presetId', presetId);
        if (limit) params.set('limit', limit);
        const query = params.toString();
        return query ? `${PROXY_PATH}?${query}` : PROXY_PATH;
      }

      async load() {
        if (this.loaded) return;
        this.loaded = true;

        try {
          const response = await fetch(this.endpoint, {
            headers: {Accept: 'application/json'},
          });
          if (!response.ok) throw new Error(`Feed responded ${response.status}`);

          const data = await response.json();
          this.render(Array.isArray(data.items) ? data.items : []);
        } catch (error) {
          console.warn('[ugc-feed]', error);
          this.fail();
        }
      }

      /**
       * Nothing to show is not an error state — the section removes itself
       * rather than leaving an empty shell in the page.
       */
      fail() {
        this.setAttribute('hidden', '');
        const section = this.closest('[data-section-type="ugc"]');
        if (section) section.setAttribute('hidden', '');
      }

      render(items) {
        if (items.length === 0) {
          this.fail();
          return;
        }

        const fragment = document.createDocumentFragment();

        items.forEach((item) => {
          const media = item.media && item.media[0];
          if (!media) return;

          const node = this.template.content.firstElementChild.cloneNode(true);
          const link = node.querySelector('[data-ugc-link]');
          const image = node.querySelector('[data-ugc-image]');
          const video = node.querySelector('[data-ugc-video]');
          const handle = node.querySelector('[data-ugc-handle]');
          const caption = node.querySelector('[data-ugc-caption]');
          const badge = node.querySelector('.ugc__badge');

          if (link) {
            if (item.permalink) {
              link.href = item.permalink;
            } else {
              link.removeAttribute('href');
            }
            link.setAttribute(
              'aria-label',
              item.creator && item.creator.handle
                ? `View post by @${item.creator.handle}`
                : 'View original post',
            );
          }

          // Videos are posters + a play affordance, not autoplaying media —
          // a wall of autoplaying clips is a real performance and a11y cost.
          if (media.type === 'VIDEO' && video) {
            video.hidden = false;
            if (image) {
              image.src = media.thumbnailUrl || media.url;
              image.alt = item.caption ? item.caption.slice(0, 120) : '';
            }
          } else if (image) {
            image.src = media.url;
            image.alt = item.caption ? item.caption.slice(0, 120) : '';
            if (media.width && media.height) {
              image.width = media.width;
              image.height = media.height;
            }
            if (video) video.remove();
          }

          if (handle) {
            handle.textContent =
              item.creator && item.creator.handle
                ? `@${item.creator.handle}`
                : '';
          }

          if (caption) {
            caption.textContent = item.caption || '';
          }

          if (badge) {
            const provider = (item.provider || '').toLowerCase();
            const icon = badge.querySelector(`[data-ugc-provider="${provider}"]`);
            if (icon) {
              icon.hidden = false;
            } else {
              // INTERNAL, or a provider we have no icon for.
              badge.remove();
            }
          }

          fragment.appendChild(node);
        });

        if (!fragment.childElementCount) {
          this.fail();
          return;
        }

        this.list.innerHTML = '';
        this.list.appendChild(fragment);
        this.classList.add('is-loaded');
        this.initArrows();
      }

      initArrows() {
        this.prev = this.querySelector('[data-ugc-prev]');
        this.next = this.querySelector('[data-ugc-next]');
        if (!this.prev || !this.next) return;

        // Page by the visible width so each click reveals a fresh set.
        const page = (direction) => {
          this.list.scrollBy({left: direction * this.list.clientWidth, behavior: 'smooth'});
        };
        this.prev.addEventListener('click', () => page(-1));
        this.next.addEventListener('click', () => page(1));

        this.updateArrows = () => {
          const max = this.list.scrollWidth - this.list.clientWidth;
          const overflowing = max > 1;
          this.prev.hidden = !overflowing;
          this.next.hidden = !overflowing;
          this.prev.disabled = this.list.scrollLeft <= 1;
          this.next.disabled = this.list.scrollLeft >= max - 1;
        };
        this.list.addEventListener('scroll', this.updateArrows, {passive: true});
        window.addEventListener('resize', this.updateArrows, {passive: true});
        this.updateArrows();
      }
    },
  );
})();
